import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {
    AlertCircleIcon,
    AlertTriangleIcon,
    Download01Icon,
    Key01Icon,
    PlusIcon,
    RefreshCw05Icon,
    Trash01Icon
} from '@/components/ui/icon/Icon'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {routes} from '@/utilities/constants/routes'
import {StyledNameCell, StyledSelectCell, StyledStatusCell, StyledUsersWrapper} from './style'
import {useGetUsers} from '@/features/users/queries/useGetUsers.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Avatar} from '@components/ui/avatar/Avatar.tsx'
import i18n, {Trans, useTranslation} from '@/translations/i18n.tsx'
import {formatLocaleDate} from '@/dayjs.ts'
import {ChangeEvent, useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {Table} from '@components/commons/table/Table.tsx'
import {RowMenu} from '@/features/users/components/row-menu/RowMenu.tsx'
import {columnNameToLabel, userStatusToLabel, useUsersSearchParams} from '@/features/users/utils.ts'
import {Helmet} from 'react-helmet'
import {
    columnVisibilitySearchParamsToPayload,
    columnVisibilityToSearchParams,
    debounce,
    errorHandler,
    filtersParamToPayload,
    getIsRowSelected,
    raise,
    sortParamToPayload,
    workSectorToLabel
} from '@utilities/helpers.ts'
import {Button} from '@components/ui/button/Button.tsx'
import {usePasswordRecovery} from '@/features/auth/services/usePasswordRecovery.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {ColumnVisibilityMenu} from '@components/commons/columns-visibility-menu/ColumnVisibilityMenu.tsx'
import {DeleteUserModal} from '@/features/users/components/delete-user-modal/DeleteUserModal.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {StatusBullet} from '@/features/users/components/status-bullet/StatusBullet.tsx'
import {TableBodyCell, TableBodyCellText} from '@components/ui/table-atoms/TableAtoms.tsx'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {RowWarningTooltip} from '@components/commons/row-warning-tootlip/RowWarningTooltip.tsx'
import {SelectColumnHeader} from '@components/ui/select-column-header/SelectColumnHeader.tsx'
import {SelectRowCheckbox} from '@components/ui/select-row-checkbox/SelectRowCheckbox.tsx'
import {httpExportUser} from '@/features/users/http/users.http.ts'
import {Badge} from '@components/ui/badge/Badge.tsx'
import {FiltersBar} from '@components/ui/filters-bar/FiltersBar.tsx'
import {TableInputSearch} from '@components/commons/table-input-search/TableInputSearch.tsx'
import {TableBodyRow} from '@components/ui/table-body-row/TableBodyRow.tsx'
import {MoreActionsCell} from '@components/ui/more-actions-cell/MoreActionsCell.tsx'
import {SelectedRowsText} from '@components/commons/selected-rows-text/SelectedRowsText.tsx'
import {UserBase, UsersColumn} from '@/features/users/types'
import {TableFilters} from '@components/commons/table-filters/TableFilters.tsx'
import {SortingMenu} from '@/components/commons/sorting-menu/SortingMenu'
import {ViewSelect} from '@/features/views/views-select/ViewSelect'
import {usePermissions} from '@/features/auth/store/store'
import {ExportModal} from '@/components/commons/export-modal/ExportModal'

const makeColumnsMap = () =>
    [
        {
            id: UsersColumn.enum.fullName,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.fullName])
        },
        {
            id: UsersColumn.enum.status,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.status])
        },
        {
            id: UsersColumn.enum.specializationId,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.specializationId])
        },
        {
            id: UsersColumn.enum.roleId,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.roleId])
        },
        {
            id: UsersColumn.enum.sector,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.sector])
        },
        {
            id: UsersColumn.enum.lastLoginAt,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.lastLoginAt])
        },
        {
            id: UsersColumn.enum.createdAt,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.createdAt])
        },
        {
            id: UsersColumn.enum.contactEmail,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.contactEmail])
        },
        {
            id: UsersColumn.enum.birthDate,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.birthDate])
        },
        {
            id: UsersColumn.enum.mobilePhone,
            label: i18n.t(columnNameToLabel[UsersColumn.enum.mobilePhone])
        }
    ] as const satisfies {id: UsersColumn; label: string}[]

export const Users = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const navigate = useNavigate()
    const columnsMap = makeColumnsMap()
    const {searchParams, setSearchParams} = useUsersSearchParams()
    const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
    const [isSelectAll, setIsSelectAll] = useState(false)
    const [unselectedUsersIds, setUnselectedUsersIds] = useState<string[]>([])
    const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([])
    const resetSelectionStates = () => {
        setIsSelectAll(false)
        setSelectedUsersIds([])
        setUnselectedUsersIds([])
    }
    const passwordRecoveryMutation = usePasswordRecovery({
        onError: error => errorHandler(error),
        onSuccess: () => {
            toast.success(t('users:reset_password_success'))
            resetSelectionStates()
        }
    })
    const inputSearchRef = useRef<HTMLInputElement | null>(null)
    const usersQuery = useGetUsers(
        {
            limit: searchParams.limit,
            page: searchParams.page,
            search: searchParams.search,
            responseType: 'index'
        },
        {
            filters: filtersParamToPayload(searchParams.filters)?.filters,
            sorts: sortParamToPayload(searchParams.sorts)
        }
    )
    const selectedUsersCount = isSelectAll
        ? usersQuery.persistedPagination.total - unselectedUsersIds.length
        : selectedUsersIds.length

    const onSearch = debounce((event: ChangeEvent<HTMLInputElement>) => {
        setSearchParams({search: event.target.value || undefined, page: 1})
    }, 1000)

    const columnVisibility =
        columnVisibilitySearchParamsToPayload(UsersColumn, searchParams.columnVisibility) ??
        Object.fromEntries(UsersColumn.options.map(column => [column, true]))

    return (
        <StyledUsersWrapper>
            <Helmet title={t('users:meta_title')} />
            <PageHeading justify="space-between">
                <h2>{t('commons:users')}</h2>
                {permissions?.users?.write && (
                    <ButtonLink variant="primaryNeutral" to={routes.CREATE_USER.path}>
                        <PlusIcon />
                        {t('commons:users')}
                    </ButtonLink>
                )}
            </PageHeading>
            <FiltersBar>
                <Flexbox align="center" gap={2}>
                    <TableInputSearch
                        ref={inputSearchRef}
                        defaultValue={searchParams.search}
                        onChange={onSearch}
                        placeholder={t('commons:search_name')}
                    />
                    {selectedUsersCount >= 1 && (
                        <>
                            <SelectedRowsText count={selectedUsersCount} />
                            {selectedUsersCount == 1 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={passwordRecoveryMutation.isPending}
                                    onClick={() => {
                                        const userEmail = usersQuery.data?.data.find(
                                            user => user.id == selectedUsersIds[0]
                                        )?.email
                                        if (userEmail) {
                                            passwordRecoveryMutation.mutate({email: userEmail})
                                        } else {
                                            toast.error(t('errors:default'))
                                        }
                                    }}
                                >
                                    <Key01Icon />
                                    {t('users:reset_password')}
                                    {passwordRecoveryMutation.isPending && <Spinner />}
                                </Button>
                            )}
                            <ExportModal
                                entity="users"
                                allowedExtensions={['xlsx', 'xls', 'csv']}
                                selectedCount={selectedUsersCount}
                                promise={({extension, mode, customColumns}) => {
                                    return httpExportUser({
                                        payload: {
                                            selectedUserIds: selectedUsersIds,
                                            exceptedUserIds: unselectedUsersIds,
                                            type: extension,
                                            filters: filtersParamToPayload(searchParams.filters)?.filters,
                                            fields:
                                                mode == 'custom'
                                                    ? customColumns
                                                    : mode == 'view'
                                                      ? UsersColumn.exclude(['select', 'moreActions']).options.filter(
                                                            column => columnsMap.some(col => col.id === column)
                                                        )
                                                      : UsersColumn.exclude(['select', 'moreActions']).options,
                                            sorts: sortParamToPayload(searchParams.sorts)
                                        }
                                    })
                                }}
                                asyncExport
                                fileName="Personale"
                                trigger={
                                    <Button variant="ghost" size="sm">
                                        <Download01Icon />
                                        {t('commons:export')}
                                    </Button>
                                }
                            />
                            {selectedUsersCount == 1 && (
                                <Button variant="ghost" size="sm" onClick={() => setIsDeleteUserModalOpen(true)}>
                                    <Trash01Icon />
                                    {t('commons:delete')}
                                </Button>
                            )}
                        </>
                    )}
                </Flexbox>
                {selectedUsersCount == 0 && <ViewSelect entity="users" searchInputRef={inputSearchRef} />}
                {selectedUsersCount == 0 && (
                    <Flexbox style={{flexShrink: 0}} align="center" gap={2}>
                        <TableFilters
                            onApply={filters => {
                                setSearchParams({filters: JSON.stringify(filters), page: 1})
                                resetSelectionStates()
                            }}
                            filtersParams={searchParams.filters}
                            entity="users"
                            availableFilters={[
                                {key: UsersColumn.enum.status, label: columnNameToLabel[UsersColumn.enum.status]},
                                {key: UsersColumn.enum.sector, label: columnNameToLabel[UsersColumn.enum.sector]},
                                {key: UsersColumn.enum.birthDate, label: columnNameToLabel[UsersColumn.enum.birthDate]},
                                {key: UsersColumn.enum.createdAt, label: columnNameToLabel[UsersColumn.enum.createdAt]},
                                {
                                    key: UsersColumn.enum.lastLoginAt,
                                    label: columnNameToLabel[UsersColumn.enum.lastLoginAt]
                                },
                                {
                                    key: UsersColumn.enum.specializationId,
                                    label: columnNameToLabel[UsersColumn.enum.specializationId]
                                },
                                {key: UsersColumn.enum.roleId, label: columnNameToLabel[UsersColumn.enum.roleId]},
                                {
                                    key: UsersColumn.enum.contactEmail,
                                    label: columnNameToLabel[UsersColumn.enum.contactEmail]
                                },
                                {
                                    key: UsersColumn.enum.mobilePhone,
                                    label: columnNameToLabel[UsersColumn.enum.mobilePhone]
                                }
                            ]}
                        />
                        <SortingMenu
                            columns={columnsMap}
                            order={sortParamToPayload(searchParams.sorts) ?? []}
                            setOrder={sorts => {
                                setSearchParams({sorts: JSON.stringify(sorts)})
                                resetSelectionStates()
                            }}
                        />
                        <ColumnVisibilityMenu
                            columns={columnsMap}
                            columnVisibility={columnVisibility}
                            onSubmit={columnVisibility =>
                                setSearchParams({columnVisibility: columnVisibilityToSearchParams(columnVisibility)})
                            }
                        />
                    </Flexbox>
                )}
            </FiltersBar>
            {usersQuery.isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState title={t('users:no_users_title')} subtitle={t('users:no_users_description')}>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setSearchParams({search: undefined, filters: undefined, view: undefined, page: 1})
                                    if (inputSearchRef.current) {
                                        inputSearchRef.current.value = ''
                                    }
                                }}
                            >
                                {t('users:no_users_cta')}
                            </Button>
                        </EmptyState>
                    }
                    rowComponent={({tableBodyRowProps, dataItem, index}) => (
                        <TableBodyRow
                            onClick={
                                dataItem && permissions?.userDetails?.read
                                    ? () => navigate(dataItem.id.toString())
                                    : undefined
                            }
                            key={index}
                            style={{cursor: dataItem && permissions?.userDetails?.read ? 'pointer' : 'default'}}
                            {...tableBodyRowProps}
                        />
                    )}
                    pagination={{
                        ...usersQuery.persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => {
                            resetSelectionStates()
                            setSearchParams({limit, page: 1})
                        }
                    }}
                    columnPinning={{
                        left: [UsersColumn.enum.select, UsersColumn.enum.fullName],
                        right: [UsersColumn.enum.moreActions]
                    }}
                    columnVisibility={columnVisibility}
                    getIsRowSelected={({dataItem}) =>
                        getIsRowSelected({
                            dataItem,
                            selectedIds: selectedUsersIds,
                            unselectedIds: unselectedUsersIds,
                            isSelectAll
                        })
                    }
                    isLoading={usersQuery.isPending}
                    data={usersQuery.data?.data ?? []}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledSelectCell>
                                        {dataItem.fieldsToCompile.length >= 1 ? (
                                            <Tooltip
                                                contentProps={{onClick: event => event.stopPropagation()}}
                                                maxWidth="320px"
                                                content={
                                                    <RowWarningTooltip
                                                        icon={RefreshCw05Icon}
                                                        title={t('errors:missing_data')}
                                                        paragraph={
                                                            <Trans
                                                                i18nKey="users:missing_data_paragraph"
                                                                components={{
                                                                    cta: <Link to={dataItem.id.toString()} />
                                                                }}
                                                            />
                                                        }
                                                    />
                                                }
                                                triggerProps={{asChild: true}}
                                                variant="light"
                                            >
                                                <RefreshCw05Icon size={16} />
                                            </Tooltip>
                                        ) : dataItem.fieldsToUpdate.length >= 1 ? (
                                            <Tooltip
                                                contentProps={{onClick: event => event.stopPropagation()}}
                                                maxWidth="320px"
                                                content={
                                                    <RowWarningTooltip
                                                        icon={AlertCircleIcon}
                                                        title={t('users:need_update_title')}
                                                        paragraph={
                                                            <Trans
                                                                i18nKey="users:need_update_paragraph"
                                                                components={{
                                                                    cta: <Link to={dataItem.id.toString()} />
                                                                }}
                                                            />
                                                        }
                                                    />
                                                }
                                                triggerProps={{asChild: true}}
                                                variant="light"
                                            >
                                                <AlertCircleIcon size={16} />
                                            </Tooltip>
                                        ) : (
                                            <></>
                                        )}
                                        {permissions?.users.write && (
                                            <SelectRowCheckbox
                                                isSelectAll={isSelectAll}
                                                setSelectedIds={setSelectedUsersIds}
                                                getIsRowSelected={({dataItem}) =>
                                                    getIsRowSelected({
                                                        dataItem,
                                                        selectedIds: selectedUsersIds,
                                                        unselectedIds: unselectedUsersIds,
                                                        isSelectAll
                                                    })
                                                }
                                                dataItem={dataItem}
                                                setUnselectedIds={setUnselectedUsersIds}
                                            />
                                        )}
                                    </StyledSelectCell>
                                </TableBodyCell>
                            ),
                            header: () =>
                                permissions?.users.write ? (
                                    <SelectColumnHeader
                                        currentPageIds={usersQuery.data?.data.map(user => user.id) ?? []}
                                        isSelectAll={isSelectAll}
                                        selectedIds={selectedUsersIds}
                                        setIsSelectAll={setIsSelectAll}
                                        unselectedIds={unselectedUsersIds}
                                        setUnselectedIds={setUnselectedUsersIds}
                                        setSelectedIds={setSelectedUsersIds}
                                        selectedCount={selectedUsersCount}
                                        paginationTotal={usersQuery.persistedPagination.total}
                                    />
                                ) : (
                                    <></>
                                ),
                            id: UsersColumn.enum.select,
                            size: () => 64
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledNameCell>
                                        <Avatar
                                            imageUrl={dataItem.avatarUrl ?? undefined}
                                            size="sm"
                                            name={dataItem.fullName}
                                        />
                                        <TableBodyCellText>{dataItem.fullName}</TableBodyCellText>
                                        {dataItem.roleName && !dataItem.lastLoginAt && (
                                            <Badge shape="rectangle" size="sm" style={{flexShrink: 0}}>
                                                {t('users:invited')}
                                            </Badge>
                                        )}
                                    </StyledNameCell>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.fullName]),
                            id: UsersColumn.enum.fullName,
                            minSize: () => 80,
                            size: () => 250,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledStatusCell variant="dark">
                                        <StatusBullet status={dataItem.status1} />
                                        {t(userStatusToLabel[dataItem.status1])}
                                    </StyledStatusCell>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.status]),
                            id: UsersColumn.enum.status,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.specializationName}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.specializationId]),
                            id: UsersColumn.enum.specializationId,
                            minSize: () => 80,
                            size: () => 140,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.roleName ? (
                                        <TableBodyCellText>{dataItem.roleName}</TableBodyCellText>
                                    ) : (
                                        <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.roleId]),
                            id: UsersColumn.enum.roleId,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.workSector ? (
                                        <TableBodyCellText>
                                            {t(workSectorToLabel[dataItem.workSector])}
                                        </TableBodyCellText>
                                    ) : (
                                        <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.sector]),
                            id: UsersColumn.enum.sector,
                            minSize: () => 140,
                            size: () => 140,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    {dataItem.lastLoginAt ? (
                                        <TableBodyCellText>
                                            {formatLocaleDate(dataItem.lastLoginAt, 'll')}
                                        </TableBodyCellText>
                                    ) : (
                                        <TableBodyCellText variant="light">{t('commons:none')}</TableBodyCellText>
                                    )}
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.lastLoginAt]),
                            id: UsersColumn.enum.lastLoginAt,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.createdAt, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.createdAt]),
                            id: UsersColumn.enum.createdAt,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.email}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.contactEmail]),
                            id: UsersColumn.enum.contactEmail,
                            minSize: () => 80,
                            size: () => 200,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{formatLocaleDate(dataItem.birthDate, 'll')}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.birthDate]),
                            id: UsersColumn.enum.birthDate,
                            size: () => 140
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.phoneNumber}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(columnNameToLabel[UsersColumn.enum.mobilePhone]),
                            id: UsersColumn.enum.mobilePhone,
                            size: () => 160,
                            maxSize: () => 400
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <RowMenu
                                        onDeleteSuccess={() => {
                                            setSelectedUsersIds(prev => prev.filter(id => id != dataItem.id))
                                            setUnselectedUsersIds(prev => prev.filter(id => id != dataItem.id))
                                        }}
                                        user={{
                                            roleName: dataItem.roleName,
                                            ...UserBase.parse(dataItem)
                                        }}
                                    />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: UsersColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
            {isDeleteUserModalOpen && (
                <DeleteUserModal
                    onSuccess={resetSelectionStates}
                    onClose={() => setIsDeleteUserModalOpen(false)}
                    user={
                        usersQuery.data?.data.find(user => user.id == selectedUsersIds[0]) ?? raise('user not selected')
                    }
                />
            )}
        </StyledUsersWrapper>
    )
}
